import React from 'react';
import { Document, Font, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';

import { dayjs } from '../../../../../../../utils/dayjs-utils';
import createCountryCodeToCountryNameMap from '../../NewContractModal/SelectGoverningCountry/helpers/createCountryCodeToCountryNameMap';
import AlexBrushFont from '../../SignContractModal/Fonts/BetterGrade-Regular.ttf';
import MsMadi from '../../SignContractModal/Fonts/MsMadi-Regular.ttf';
import ReactPdfPaymentTermsFromTemplate from './ReactPdfPaymentTermsFromTemplate';
import ReactPdfScopeOfContentFromTemplate from './ReactPdfScopeOfContentFromTemplate';

const countryCodeToCountryNameMap = createCountryCodeToCountryNameMap();

Font.register({
  family: 'Lato Bold',
  src: 'https://fonts.gstatic.com/s/lato/v16/S6u9w4BMUTPHh6UVSwiPHA.ttf',
});
Font.register({
  family: 'Gupter',
  fonts: [
    { src: 'https://fonts.gstatic.com/s/gupter/v14/2-cm9JNmxJqPO1QUYQ.ttf', fontWeight: 400 },
    { src: 'https://fonts.gstatic.com/s/gupter/v14/2-cl9JNmxJqPO1Qslb-bUg.ttf', fontWeight: 500 },
    { src: 'https://fonts.gstatic.com/s/gupter/v14/2-cl9JNmxJqPO1Qs3bmbUg.ttf', fontWeight: 700 },
  ],
});

// for type signature
Font.register({
  family: 'Dancing Script',
  src: 'https://fonts.gstatic.com/s/dancingscript/v25/If2cXTr6YS-zF4S-kcSWSVi_sxjsohD9F50Ruu7BMSoHTQ.ttf',
});
Font.register({
  family: 'Caveat',
  src: 'https://fonts.gstatic.com/s/caveat/v18/WnznHAc5bAfYB2QRah7pcpNvOx-pjfJ9SII.ttf',
});

Font.register({
  family: 'Lobster',
  src: 'https://fonts.gstatic.com/s/lobster/v30/neILzCirqoswsqX9_oU.ttf',
});
Font.register({
  family: 'Signika Negative',
  src: 'https://fonts.gstatic.com/s/signikanegative/v21/E21x_cfngu7HiRpPX3ZpNE4kY5zKSPmJXkF0VDD2RAqnS73s.ttf',
});
Font.register({
  family: 'Ubuntu Sans Mono',
  src: 'https://fonts.gstatic.com/s/ubuntusansmono/v1/jVyc7mzgBHrR5yE7ZyRg0QRJMKI4zAbgjc1t-pKe27Ev_kYR.ttf',
});
Font.register({
  family: 'Pacifico',
  src: 'https://fonts.gstatic.com/s/pacifico/v22/FwZY7-Qmy14u9lezJ96A.ttf',
});
Font.register({
  family: 'Patrick Hand',
  src: 'https://fonts.gstatic.com/s/patrickhand/v23/LDI1apSQOAYtSuYWp8ZhfYeMWQ.ttf',
});
Font.register({
  family: 'Alex Brush',
  src: AlexBrushFont,
});
Font.register({
  family: 'Ms Madi',
  src: MsMadi,
});

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Gupter',
    fontWeight: 500,
  },
  contractTitle: {
    textAlign: 'center',
    fontSize: 18,
    fontFamily: 'Gupter',
    fontWeight: 700,
    marginBottom: 24,
  },
  contractParagraph: {
    fontSize: 14,
    marginBottom: 16,
  },
  subHeading: {
    fontFamily: 'Gupter',
    fontWeight: 700,
    marginTop: 24,
    marginBottom: 16,
    fontSize: 16,
  },
  boldText: {
    fontFamily: 'Gupter',
    fontWeight: 700,
  },
  pageBreak: {
    pageBreak: 'before', // Ensures the text starts on a new page if needed
  },
});

/*
  signatureMeta
  {
      type: 'typed',
      text: typeSignatureFormData?.name,
      fontFamily: typeSignatureFormData?.selectedStyle,
      _id: string
  };
  {
      type: 'image',
      src: string,
      _id: string
  };
  */
export default function ContractReactPdfPreview({
  formData,
  meta,
  userSignatureMeta,
  influencerSignatureMeta,
  influencerSignedAt,
}) {

  const getUsageRightsText = () => {
    if (formData?.showUsageRights) {
      if (formData.usageRights === 'Forever') {
        return ' in perpetuity';
      } else {
        return ` for ${formData?.usageRights}`;
      }
    } else {
      return null;
    }
  };

  return (
    <Document>
      <Page size={'A4'} break style={styles.page}>
        <View fixed>
          <Image src="/logo.png" style={{ width: 100 }} />
        </View>

        <View style={{ marginTop: 24 }}>
          <Text style={styles.contractTitle}>
            Partnership Agreement: {formData?.influencerName} + {formData?.brandName}
          </Text>

          <Text style={styles.contractParagraph}>
            This Influencer Contract ("Agreement") is made and entered into on{' '}
            {dayjs(formData?.startDate)?.format('LL')} between {formData?.brandName} ("Company"), and{' '}
            <Text style={styles.boldText}>{formData?.influencerName}</Text>
            ("Influencer").
          </Text>

          <Text style={styles.subHeading}>Background</Text>

          <Text style={styles.contractParagraph}>
            The Company wishes to engage the services of the Influencer for the purpose of promoting the Company's
            products and services through social media platforms.
          </Text>

          <Text style={styles.contractParagraph}>
            The Influencer is an individual who has developed a significant following on social media platforms and has
            the ability to influence purchasing decisions of their followers.
          </Text>

          <Text style={styles.subHeading}>Series Overview</Text>

          <Text style={styles.contractParagraph}>
            The Influencer agrees to provide the following services to the Company:
          </Text>
          <ListItem>
            <Text style={{ fontSize: 14 }}>Following the guidelines mentioned under “Scope of Content” below.</Text>
          </ListItem>
          <ListItem>
            <Text style={{ fontSize: 14 }}>
              Creating and/or posting content on the social media platforms agreed upon by both parties.
            </Text>
          </ListItem>
          <ListItem>
            <Text style={{ fontSize: 14 }}>
              Including the Company’s products or services in at least [number] posts per month on the agreed upon
              social media platforms.
            </Text>
          </ListItem>
          <ListItem>
            <Text style={{ fontSize: 14 }}>
              Using specific hashtags and mentions in posts related to the Company’s products or services.
            </Text>
          </ListItem>
          <ListItem>
            <Text style={{ fontSize: 14 }}>
              This Contract shall start on {dayjs(formData?.startDate)?.format('LL')} and shall continue until the
              termination or completion of the services
              {formData?.endDate ? `, which is expected by ${dayjs(formData?.endDate)?.format('LL')}.` : '.'}
            </Text>
          </ListItem>

          <Text style={styles.subHeading}>Scope of Content</Text>

          {formData?.contractTemplate ? (
            <ReactPdfScopeOfContentFromTemplate
              meta={meta}
              formData={formData}
              contractTemplate={formData?.contractTemplate}
            />
          ) : (
            <Text style={styles.contractParagraph}>{formData?.scopeOfContent}</Text>
          )}

          <Text style={styles.subHeading}>Compensation & Terms</Text>

          {formData?.contractTemplate ? (
            <ReactPdfPaymentTermsFromTemplate
              meta={meta}
              contractTemplate={formData?.contractTemplate}
              formData={formData}
            />
          ) : (
            <Text style={styles.contractParagraph}>{formData?.paymentTerms}</Text>
          )}

          <Text style={styles.subHeading}>Ownership and Use of Content</Text>

          <Text style={styles.contractParagraph}>
            The Influencer grants the Company the non-exclusive, worldwide, royalty-free right to use, reproduce,
            modify, distribute, and display any content created by the Influencer related to the Company’s products or
            services on the Company’s website, social media platforms, or any other marketing materials
            {getUsageRightsText()}.
          </Text>

          <Text style={styles.contractParagraph}>
            The Influencer represents and warrants that any content created by the Influencer is original and does not
            infringe upon any third-party rights.
          </Text>
        </View>

        <View wrap={false}>
          <Text style={styles.subHeading}>FTC Compliance</Text>

          <Text style={styles.contractParagraph}>
            The Influencer shall comply with all applicable laws, rules, and regulations including the Federal Trade
            Commission (FTC) guidelines regarding endorsements and testimonials in advertising. The Influencer shall
            clearly and conspicuously disclose in any post or content created in connection with this Agreement that
            they are being compensated by the Company for promoting the Company’s products or services.
          </Text>
        </View>

        <View wrap={false}>
          <Text style={styles.subHeading}>Confidentiality</Text>

          <Text style={styles.contractParagraph}>
            The Influencer agrees to keep confidential all non-public information provided by the Company related to the
            Company’s business, products, and services. The Influencer shall not disclose any such information to any
            third party without the prior written consent of the Company.
          </Text>
        </View>

        <View wrap={false}>
          <Text style={styles.subHeading}>Termination</Text>

          <Text style={styles.contractParagraph}>
            This Agreement may be terminated by either party upon 7 days written notice to the other party. In the event
            of termination, the Influencer shall be entitled to payment for all services provided up to the date of
            termination.  
          </Text>

          <Text style={styles.contractParagraph}>
            This agreement may be terminated upon breach of services or terms mentioned herein the contract.
          </Text>

          <Text style={styles.contractParagraph}>
            This agreement will be terminated upon successful completion of services by the Influencer and completion of
            payment by the Company.
          </Text>
        </View>

        <View wrap={false}>
          <Text style={styles.subHeading}>Independent Contractor</Text>

          <Text style={styles.contractParagraph}>
            The Influencer shall perform all services as an independent contractor and not as an employee of the
            Company. The Influencer shall be responsible for all taxes, insurance, and other expenses related to the
            services provided under this agreement.
          </Text>
        </View>

        <View wrap={false}>
          <Text style={styles.subHeading}>Exclusivity</Text>

          <Text style={styles.contractParagraph}>
            The Influencer agrees to work exclusively with the Company for the duration of this Agreement. During the
            term of this Agreement, the Influencer shall not promote any products or services that directly compete with
            the Company's products or services without the prior written consent of the Company. 
          </Text>

          <Text style={styles.contractParagraph}>
            The Influencer acknowledges that any breach of this exclusivity clause may result in irreparable harm to the
            Company, and the Company shall be entitled to seek injunctive relief and any other available remedies for
            such breach.
          </Text>
        </View>

        <View wrap={false}>
          <Text style={styles.subHeading}>Governing Law and Venue</Text>

          <Text style={styles.contractParagraph}>
            This Agreement shall be governed by and construed in accordance with the laws of{' '}
            {countryCodeToCountryNameMap[formData?.governingCountry]} without regard to conflicts of law principles. Any
            legal action arising from or relating to this Agreement shall be brought in the state or federal courts
            located in {countryCodeToCountryNameMap[formData?.governingCountry]}. 
          </Text>
        </View>

        <View wrap={false}>
          <Text style={styles.subHeading}>Entire Agreement</Text>

          <Text style={styles.contractParagraph}>
            This Agreement constitutes the entire understanding between the Company and the Influencer and supersedes
            all prior negotiations, understandings, and agreements between the parties. This Agreement may not be
            amended or modified except in writing.
          </Text>

          <Text style={styles.contractParagraph}>
            IN WITNESS WHEREOF, the parties have executed this Agreement as of the date first written above.
          </Text>
        </View>

        <View wrap={false} style={{ marginTop: 40 }}>
          <Signature signatureMeta={userSignatureMeta} />
          <Text>___________________</Text>
          <Text style={{ fontFamily: 'Lato Bold', fontSize: 14, marginTop: 16 }}>
            {formData?.influencerManagerFullName ? `${formData?.influencerManagerFullName}, ` : ''}{' '}
            {formData?.brandName}
          </Text>
          <Text style={{ fontFamily: 'Lato Bold', fontSize: 14, marginTop: 16 }}>
            Signed on: {dayjs(formData?.userSignedOn)?.format('DD-MM-YYYY')}
          </Text>
        </View>

        <View wrap={false} style={{ marginTop: 120 }}>
          {influencerSignatureMeta ? <Signature signatureMeta={influencerSignatureMeta} /> : null}
          {/* <Text>abcd sign</Text> */}
          <Text>___________________</Text>
          <Text style={{ fontFamily: 'Lato Bold', fontSize: 14, marginTop: 16 }}>{formData?.influencerName}</Text>
          <Text style={{ fontFamily: 'Lato Bold', fontSize: 14, marginTop: 16 }}>
            Signed on: {influencerSignedAt ? dayjs(influencerSignedAt)?.format('DD-MM-YYYY') : 'Pending'}
          </Text>
        </View>
      </Page>
    </Document>
  );
}

function Signature({ signatureMeta }) {
  if (signatureMeta?.type === 'typed' && signatureMeta?.text && signatureMeta?.fontFamily) {
    return <Text style={{ fontFamily: signatureMeta?.fontFamily, fontSize: 20 }}>{signatureMeta?.text}</Text>;
  }
  if (signatureMeta?.type === 'image' && signatureMeta?.src) {
    return <Image src={signatureMeta?.src} style={{ maxWidth: 150 }} />;
  }

  return <Text></Text>;
}

function ListItem({ children }) {
  return (
    <View style={{ flexDirection: 'row', marginBottom: 4 }}>
      <Text style={{ marginHorizontal: 8 }}>•</Text>
      {children}
    </View>
  );
}
